import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { manifestationsApi, storiesApi } from '@/services/stories';
import type {
  BulkManifestationsResult,
  ManifestationCreate,
  ManifestationKind,
  StoryCreate,
  StoryUpdate,
} from '@/types/story';

export const storyKeys = {
  all: ['stories'] as const,
  list: (params: object) => [...storyKeys.all, 'list', params] as const,
  detail: (id: string) => [...storyKeys.all, 'detail', id] as const,
  byRef: (kind: string, externalId: string) =>
    [...storyKeys.all, 'by-ref', kind, externalId] as const,
};

export function useStories(params: { status?: string; search?: string; limit?: number } = {}) {
  return useQuery({
    queryKey: storyKeys.list(params),
    queryFn: () => storiesApi.list(params),
  });
}

export function useStory(id: string | undefined) {
  return useQuery({
    queryKey: storyKeys.detail(id ?? ''),
    queryFn: () => storiesApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useStoriesByRef(
  kind: ManifestationKind | undefined,
  externalId: string | undefined,
) {
  return useQuery({
    queryKey: storyKeys.byRef(kind ?? '', externalId ?? ''),
    queryFn: () => manifestationsApi.findByRef(kind!, externalId!),
    enabled: Boolean(kind && externalId),
    staleTime: 30_000,
  });
}

export function useCreateStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StoryCreate) => storiesApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: storyKeys.all }),
  });
}

export function useUpdateStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: StoryUpdate }) =>
      storiesApi.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: storyKeys.detail(id) });
      qc.invalidateQueries({ queryKey: storyKeys.all });
    },
  });
}

export function useDeleteStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storiesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: storyKeys.all }),
  });
}

export function useAddManifestation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storyId, payload }: { storyId: string; payload: ManifestationCreate }) =>
      storiesApi.addManifestation(storyId, payload),
    onSuccess: (_data, { storyId }) => {
      qc.invalidateQueries({ queryKey: storyKeys.detail(storyId) });
      qc.invalidateQueries({ queryKey: storyKeys.all });
    },
  });
}

export function useBulkAddManifestations() {
  const qc = useQueryClient();
  return useMutation<
    BulkManifestationsResult,
    Error,
    { storyId: string; items: ManifestationCreate[] }
  >({
    mutationFn: ({ storyId, items }) =>
      storiesApi.bulkAddManifestations(storyId, items),
    onSuccess: (_data, { storyId }) => {
      qc.invalidateQueries({ queryKey: storyKeys.detail(storyId) });
      qc.invalidateQueries({ queryKey: storyKeys.all });
    },
  });
}

export function useRemoveManifestation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      storyId,
      manifestationId,
    }: {
      storyId: string;
      manifestationId: string;
    }) => storiesApi.removeManifestation(storyId, manifestationId),
    onSuccess: (_data, { storyId }) => {
      qc.invalidateQueries({ queryKey: storyKeys.detail(storyId) });
    },
  });
}
